import { useContext, useEffect, useState } from "react";
import SimpleDataTable from "lib/components/common/simple-data-table";
import { TableColumn } from "react-data-table-component";
import useClinics from "lib/hooks/useClinics";
import { Button } from "react-bootstrap";
import ClinicDetails from "./clinic-details";
import { isItemInArray } from "lib/utils/array-utils";
import { getUniqueId, isSuperAdminUser } from "lib/utils/util";
import { AppContext } from "lib/contexts/app-context";
import { DEFAULT_CLINIC } from "lib/constants/default-values";
import useRooms from "lib/hooks/useRooms";

const columns: TableColumn<Clinic>[] = [
  {
    name: "Sr. No.",
    selector: (row: Clinic) => row.index ?? 0,
    sortable: true,
    width: "10%",
    center: true,
  },
  {
    name: "Clinic",
    selector: (row: Clinic) => row.name,
    sortable: true,
    width: "auto",
  },
  {
    name: "Postcode",
    selector: (row: Clinic) => row.postCode,
    sortable: true,
    width: "15%",
  },
  {
    name: "Available Rooms",
    selector: (row: Clinic) => row.noOfAvailableRooms,
    sortable: true,
    width: "15%",
    center: true,
  },
  {
    name: "Enable Session Booking",
    selector: (row) => (row as any).enableSessionBooking ? "Yes" : "No",
    sortable: true,
    width: "20%",
    center: true,
  },
  {
    name: "Closed On Sundays",
    selector: (row) => (row as any).sundayClose ? "Yes" : "No",
    sortable: true,
    width: "15%",
    center: true,
  },
  {
    name: "Site ID",
    selector: (row) => (row as any).siteId ?? "-",
    sortable: true,
    width: "10%",
    center: true,
  }
];

const Clinics = () => {
  const {
    clinics,
    getClinics,
    getCalenderDetails,
    updateCalendarDetails,
    error,
    loading,
    updateClinic,
    addClinic,
    getClinicProfilePic,
    updateClinicProfilePic,
  } = useClinics();

  const {
    rooms,
    addRoom,
    updateRoom,
    deleteRoom,
    getRooms,
    getRoomImages,
    deleteRoomImages,
    updateRoomImages,
    loading: roomsLoading,
    error: roomsError,
  } = useRooms();

  const { appState } = useContext<AppState>(AppContext);
  const [currentClinic, setCurrentClinic] = useState<Clinic | null>(null);
  const [currentClinicCalendarDetails, setCurrentClinicCalendarDetails] =
    useState<CalendarDetails | null>(null);
  const isSuperAdmin = isSuperAdminUser(appState?.userRole);

  const refreshClinics = () => {
    getClinics(isSuperAdmin);
    setCurrentClinicCalendarDetails(null);
    setCurrentClinic(null);
  };
  const [clinicProfilePic, setClinicProfilePic] = useState<string | null>(null);

  const [selectedRoom, setSelectedRoom] = useState<RoomDetails | null>(null);

  useEffect(() => {
    refreshClinics();
  }, []);

  const uploadProfilePic = (pic: DMSDocument) => {
    updateClinicProfilePic(currentClinic?.id, pic.document, refreshClinics);
  };

  const handleClinicUpdate = (
    value: Clinic,
    calendarValue: CalendarDetails,
    clinicProfilePic: DMSDocument
  ) => {
    if (isItemInArray(clinics, "id", value.id)) {
      updateClinic(value, () =>
        updateCalendarDetails(
          value.id,
          { ...calendarValue, parentType: "clinic", parentId: value.id },
          () => {
            if (clinicProfilePic?.document) {
              uploadProfilePic(clinicProfilePic);
            } else {
              refreshClinics();
            }
          }
        )
      );
    } else {
      addClinic({ ...value, ...calendarValue }, () => {
        if (clinicProfilePic?.document) {
          uploadProfilePic(clinicProfilePic);
        } else {
          refreshClinics();
        }
      });
    }
  };

  const handleClinicRoomUpdate = (
    roomId?: string,
    roomDetails?: RoomDetails
  ) => {
    // If roomId is not provided, it means a new room is being added
    if (!roomId && roomDetails) {
      if (roomDetails) {
        addRoom(currentClinic?.id, roomDetails, (value) => {
          // Upload images if provided

          if (roomDetails.images?.length > 0) {
            updateRoomImages(
              currentClinic?.id,
              value.id,
              roomDetails.images,
              () => {
                //getRoomImages(currentClinic?.id, value.id);
                setSelectedRoom(null);
                getRooms(currentClinic?.id);
              }
            );
          } else {
            setSelectedRoom(null);
            getRooms(currentClinic?.id);
          }
        });
      }
      // If roomId is provided but roomDetails is not, it means the room is being deleted
    } else if (roomId && !roomDetails) {
      // If roomDetails is not provided, it means the room is being deleted
      console.log("Deleting room with ID:", roomId);

      deleteRoom(roomId, () => {
        // Delete images associated with the room if any
        deleteRoomImages(currentClinic?.id, roomId);
        setSelectedRoom(null);
        getRooms(currentClinic?.id);
      });
    } else if (roomId && roomDetails) {
      // If both roomId and roomDetails are provided, it means the room is being updated
      updateRoom(roomId, roomDetails, () => {
        // Upload images if provided
        if (roomDetails.images?.length > 0) {
          // Handle image upload logic here
          updateRoomImages(
            currentClinic?.id,
            roomDetails.id,
            roomDetails.images,
            () => {
              //getRoomImages(currentClinic?.id, roomDetails.id);
              setSelectedRoom(null);
              getRooms(currentClinic?.id);
            }
          );
        } else {
          setSelectedRoom(null);
          deleteRoomImages(currentClinic?.id, roomId);
          getRooms(currentClinic?.id);
        }
      });
    } else {
      // If neither roomId nor roomDetails is provided, throw an error
    }
  };

  const handleClinicSelection = (clinic?: Clinic) => {
    if (clinic) {
      getCalenderDetails(clinic.id, (calendarDetails) => {
        setCurrentClinicCalendarDetails(calendarDetails);
        setCurrentClinic(clinic);
        getRooms(clinic?.id);
        getClinicProfilePic(clinic?.id, (profilePic) => {
          console.log("profilePic", profilePic);
          if (profilePic) {
            setClinicProfilePic(profilePic);
          } else {
            setClinicProfilePic(null);
          }
        });
      });
    } else {
      setCurrentClinicCalendarDetails(null);
      setCurrentClinic({ ...DEFAULT_CLINIC, id: getUniqueId() });
    }
  };

  const handleRoomSelection = (room?: RoomDetails) => {
    setSelectedRoom(room ? { ...room, images: [] } : null);

    if (room && room?.id !== "") {
      getRoomImages(currentClinic?.id, room?.id, (images) =>
        setSelectedRoom({ ...room, images: images })
      );
    }
  };

  if (currentClinic) {
    return (
      <ClinicDetails
        calendarDetails={currentClinicCalendarDetails}
        clinic={currentClinic}
        clinicProfilePicUrl={clinicProfilePic}
        error={error || roomsError}
        imagesLoading={roomsLoading}
        loading={loading}
        selectedRoom={selectedRoom}
        //selectedRoomImages={roomImages}
        rooms={rooms}
        onCancelClick={() => setCurrentClinic(null)}
        onClinicSelected={getRooms}
        onUpdateClinic={handleClinicUpdate}
        onUpdateRoom={handleClinicRoomUpdate}
        onRoomSelected={handleRoomSelection}
      />
    );
  }

  return (
    <>
      <SimpleDataTable
        title="Clinics"
        action={
          appState?.userRole !== "receptionist" ? (
            <Button variant="primary" onClick={() => handleClinicSelection()}>
              Add Clinic
            </Button>
          ) : null
        }
        columns={columns}
        data={clinics}
        onRowClick={(row) => handleClinicSelection(row as Clinic)}
        error={error}
        isLoading={loading}
      />
    </>
  );
};
export default Clinics;
